import { useShallow } from 'zustand/react/shallow';
import { type RootStore, useAppStore } from '@/store';
import type { ReviewPageState } from './slice';
/**
 * Performance-optimized selectors for the review page slice
 * Use these instead of accessing the store directly for better performance
 */

// Review page data selectors
export const useReviewPageTripDetails = () =>
	useAppStore((state) => state.reviewPage.tripDetails);
export const useReviewPageContactDetails = () =>
	useAppStore((state) => state.reviewPage.contactDetails);
export const useReviewPageFarePolicies = () =>
	useAppStore((state) => state.reviewPage.farePolicies);

// Review page actions selector
export const useReviewPageActions = () =>
	useAppStore((state) => state.reviewPageActions);

// Combined selectors for common use cases
export const useReviewPageState = () =>
	useAppStore((state) => state.reviewPage);

function getFareDetails({
	selectedCab,
	coupon,
	pricing: couponPricing,
	farePolicies,
	burnOpted,
}: ReviewPageState) {
	const pricing = couponPricing || selectedCab?.pricing;
	return selectedCab && pricing
		? {
				pricing,
				coupon,
				selectedCab,
				farePolicies,
				burnOpted,
				totalPrice: burnOpted
					? pricing.displayPrice
					: pricing.displayPriceWithoutBurn,
			}
		: {};
}

type FareDetails = ReturnType<typeof getFareDetails>;

export function useFareDetails(): FareDetails;
export function useFareDetails<TResult>(
	selector: (state: FareDetails) => TResult,
): TResult;
export function useFareDetails<TResult>(
	selector?: (state: FareDetails) => TResult,
): TResult | FareDetails {
	return useAppStore(
		useShallow((state) => {
			const base = getFareDetails(state.reviewPage);
			return selector ? selector(base) : base;
		}),
	);
}

/**
 * Get complete review page data from store instance
 * For non-hook contexts like loaders - pass useAppStore.getState() as parameter
 *
 * @param store - The store instance from useAppStore.getState()
 * @returns Complete review page data with all required fields
 */
export const getReviewPageData = (store: RootStore) => {
	const { tripDetails, farePolicies, contactDetails } = store.reviewPage;
	if (!tripDetails || !farePolicies) {
		return null;
	}
	return {
		data: {
			tripDetails: tripDetails,
			farePolicies: farePolicies,
			contactDetails: contactDetails,
		},
	};
};

export function getBookingState() {
	return useAppStore.getState().reviewPage;
}
